import { ReactNode, useState } from 'react'
import { ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react'

// Clean, reusable search/filter bar shared by both Audit Log tabs. One card:
// a primary row (labelled search + key selects + Search button), a thin action
// row (advanced toggle · view-as slot · clear), and a collapsible advanced grid.
// No nested boxes — fields are a borderless label over a single bordered control.
export type FbOption = { value: string; label: string }
export type FbField =
  | { key: string; label: string; value: string; kind: 'select'; options: FbOption[] }
  | { key: string; label: string; value: string; kind: 'datetime' }

export function AuditFilterBar({
  search,
  searchPlaceholder = 'ค้นหา…',
  primary = [],
  advanced = [],
  loading = false,
  onField,
  onSearch,
  onReset,
  extra,
}: {
  search: string
  searchPlaceholder?: string
  primary?: FbField[]
  advanced?: FbField[]
  loading?: boolean
  onField: (key: string, value: string) => void
  onSearch: () => void
  onReset: () => void
  extra?: ReactNode
}) {
  const [open, setOpen] = useState(false)

  const renderField = (fld: FbField) => (
    <label className="fb-field" key={fld.key}>
      <span>{fld.label}</span>
      {fld.kind === 'select' ? (
        <select value={fld.value} onChange={(e) => onField(fld.key, e.target.value)}>
          {fld.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input type="datetime-local" value={fld.value} onChange={(e) => onField(fld.key, e.target.value)} />
      )}
    </label>
  )

  return (
    <div className="panel filter-bar">
      <div className="fb-primary">
        <label className="fb-field fb-grow">
          <span>ค้นหา</span>
          <div className="fb-search">
            <Search size={15} />
            <input
              value={search}
              placeholder={searchPlaceholder}
              spellCheck={false}
              onChange={(e) => onField('search', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSearch()
              }}
            />
          </div>
        </label>
        {primary.map(renderField)}
        <button className="btn-primary fb-go" disabled={loading} onClick={onSearch}>
          <Search size={15} /> {loading ? 'กำลังค้นหา…' : 'ค้นหา'}
        </button>
      </div>

      {(advanced.length > 0 || extra) && (
        <div className="fb-bar">
          {advanced.length > 0 && (
            <button className="fb-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              <SlidersHorizontal size={14} /> ค้นหาขั้นสูง
              <ChevronDown size={14} className={`fb-chev${open ? ' open' : ''}`} />
            </button>
          )}
          {extra && <div className="fb-extra">{extra}</div>}
          <button className="fb-clear" onClick={onReset}>
            <X size={13} /> ล้างตัวกรอง
          </button>
        </div>
      )}

      {open && advanced.length > 0 && <div className="fb-advanced">{advanced.map(renderField)}</div>}
    </div>
  )
}
